import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, ArrowRight, Loader2, Check, Mail } from 'lucide-react';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { auth, googleProvider, signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword } from '../config/firebase';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SPORTS_OPTIONS = ['Football', 'Padel', 'Pickleball', 'Cricket'];

const OTPLoginModal = ({ onClose, onSuccess }) => {
  const { sendOTP, verifyOTP, completeProfile, onLoginSuccess } = useCustomerAuth();
  const [step, setStep] = useState('phone'); // phone | otp | google-email | add-phone | signup
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [verifyResult, setVerifyResult] = useState(null);

  // Firebase auth state
  const [firebaseToken, setFirebaseToken] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);

  // Email login state
  const [emailMode, setEmailMode] = useState('login'); // login | signup
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Phone collection for Google/Email users
  const [collectPhone, setCollectPhone] = useState('');
  const [collectPhoneOtp, setCollectPhoneOtp] = useState('');
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);

  // Signup fields
  const [name, setName] = useState('');
  const [gender, setGender] = useState('');
  const [selectedSports, setSelectedSports] = useState([]);
  const [signupEmail, setSignupEmail] = useState('');
  const [age, setAge] = useState('');

  const phoneInputRef = useRef(null);
  const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    if (step === 'phone') setTimeout(() => phoneInputRef.current?.focus(), 100);
  }, [step]);

  useEffect(() => {
    if (step === 'otp') setTimeout(() => otpRefs[0].current?.focus(), 100);
  }, [step]);

  useEffect(() => {
    if (resendTimer > 0) {
      const t = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendTimer]);

  const handleSendOTP = async () => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length !== 10) {
      toast({ title: 'Error', description: 'Enter a valid 10-digit mobile number', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await sendOTP(cleaned);
      setStep('otp');
      setResendTimer(30);
      toast({ title: 'OTP Sent', description: `OTP sent to +91 ${cleaned}` });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    if (value.length > 1) value = value[value.length - 1];
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 3) otpRefs[index + 1].current?.focus();
    if (value && index === 3 && newOtp.every(d => d)) handleVerifyOTP(newOtp.join(''));
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) otpRefs[index - 1].current?.focus();
  };

  const handleOTPPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pasted.length === 4) {
      setOtp(pasted.split(''));
      otpRefs[3].current?.focus();
      handleVerifyOTP(pasted);
    }
  };

  const handleLoginDone = useCallback((userData) => {
    if (onSuccess) onSuccess(userData);
    onLoginSuccess(userData);
  }, [onSuccess, onLoginSuccess]);

  const handleVerifyOTP = useCallback(async (otpString) => {
    const cleaned = phone.replace(/\D/g, '');
    setLoading(true);
    try {
      const result = await verifyOTP(cleaned, otpString);
      setVerifyResult(result);
      if (result.customer.is_new) {
        setStep('signup');
      } else {
        handleLoginDone(result.customer);
        toast({ title: 'Welcome back!', description: `Logged in as ${result.customer.name || result.customer.phone}` });
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
      setOtp(['', '', '', '']);
      otpRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  }, [phone, verifyOTP, handleLoginDone]);

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    try {
      const cleaned = phone.replace(/\D/g, '');
      await sendOTP(cleaned);
      setResendTimer(30);
      setOtp(['', '', '', '']);
      toast({ title: 'OTP Resent' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // ── Firebase Auth Handlers ──

  const processFirebaseAuth = async (fbUser) => {
    try {
      const token = await fbUser.getIdToken();
      setFirebaseToken(token);
      setFirebaseUser(fbUser);

      // Sync with our backend
      const res = await fetch(`${API_URL}/api/auth/firebase`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Authentication failed');

      // Store customer token
      localStorage.setItem('ps_customer_token', data.token);
      localStorage.setItem('ps_customer_data', JSON.stringify(data.customer));

      if (data.needs_phone) {
        // Need to collect phone number
        setName(data.customer.name || fbUser.displayName || '');
        setSignupEmail(data.customer.email || fbUser.email || '');
        setStep('add-phone');
      } else if (data.customer.is_new) {
        setVerifyResult(data);
        setName(data.customer.name || fbUser.displayName || '');
        setSignupEmail(data.customer.email || fbUser.email || '');
        setStep('signup');
      } else {
        handleLoginDone(data.customer);
        toast({ title: 'Welcome!', description: `Signed in as ${data.customer.name || data.customer.email}` });
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await processFirebaseAuth(result.user);
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        toast({ title: 'Error', description: err.message, variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async () => {
    if (!email || !password) {
      toast({ title: 'Error', description: 'Enter email and password', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      let result;
      if (emailMode === 'signup') {
        result = await createUserWithEmailAndPassword(auth, email, password);
      } else {
        result = await signInWithEmailAndPassword(auth, email, password);
      }
      await processFirebaseAuth(result.user);
    } catch (err) {
      let msg = err.message;
      if (err.code === 'auth/email-already-in-use') msg = 'Email already registered. Try logging in.';
      else if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') msg = 'Invalid email or password.';
      else if (err.code === 'auth/user-not-found') msg = 'No account found. Try signing up.';
      else if (err.code === 'auth/weak-password') msg = 'Password must be at least 6 characters.';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSendAddPhoneOtp = async () => {
    const cleaned = collectPhone.replace(/\D/g, '');
    if (cleaned.length !== 10) {
      toast({ title: 'Error', description: 'Enter a valid 10-digit number', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await sendOTP(cleaned);
      setPhoneOtpSent(true);
      setResendTimer(30);
      toast({ title: 'OTP Sent', description: `OTP sent to +91 ${cleaned}` });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddPhone = async () => {
    const cleaned = collectPhone.replace(/\D/g, '');
    if (cleaned.length !== 10) {
      toast({ title: 'Error', description: 'Enter a valid 10-digit number', variant: 'destructive' });
      return;
    }
    const otpCode = collectPhoneOtp.replace(/\D/g, '');
    if (otpCode.length < 4) {
      toast({ title: 'Error', description: 'Enter the OTP sent to your phone', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const token = firebaseToken || await firebaseUser?.getIdToken();
      const res = await fetch(`${API_URL}/api/auth/firebase/add-phone`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cleaned, otp: otpCode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to add phone');

      // Update stored token with phone-linked one
      localStorage.setItem('ps_customer_token', data.token);

      // Check if profile needs completion
      const customerData = JSON.parse(localStorage.getItem('ps_customer_data') || '{}');
      customerData.phone = cleaned;
      localStorage.setItem('ps_customer_data', JSON.stringify(customerData));

      if (customerData.is_new && !customerData.name) {
        setPhone(cleaned);
        setStep('signup');
      } else {
        handleLoginDone({ ...customerData, phone: cleaned });
        toast({ title: 'Welcome!', description: 'Phone number added successfully' });
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteProfile = async () => {
    if (!name.trim()) {
      toast({ title: 'Error', description: 'Please enter your name', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const result = await completeProfile({
        name: name.trim(),
        gender: gender || undefined,
        sports: selectedSports.length > 0 ? selectedSports : undefined,
        email: signupEmail.trim() || undefined,
        age: age ? parseInt(age) : undefined
      });
      handleLoginDone(result.customer);
      toast({ title: 'Welcome!', description: 'Profile created successfully' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const toggleSport = (sport) => {
    setSelectedSports(prev => prev.includes(sport) ? prev.filter(s => s !== sport) : [...prev, sport]);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" data-testid="otp-login-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-300">
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-slate-100 transition z-10" data-testid="close-login-modal">
          <X className="w-5 h-5 text-slate-500" />
        </button>

        <div className="p-6 pt-8">

          {/* ── STEP: Phone Number ── */}
          {step === 'phone' && (
            <div data-testid="step-phone">
              <div className="text-center mb-4">
                <span className="font-logo text-lg font-black text-[#0A2C93]">PlaySpaces</span>
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-1">Continue to Book</h2>
              <p className="text-sm text-slate-500 mb-6">Enter your mobile number to get started</p>

              <label className="text-sm font-medium text-slate-700 mb-2 block">Mobile Number</label>
              <div className="flex gap-2 mb-6">
                <div className="flex items-center px-3.5 bg-slate-100 rounded-xl text-sm font-medium text-slate-600 border border-slate-200">+91</div>
                <input ref={phoneInputRef} type="tel" inputMode="numeric" value={phone}
                  onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  onKeyDown={e => e.key === 'Enter' && phone.length === 10 && handleSendOTP()}
                  placeholder="Enter 10-digit number"
                  className="flex-1 px-4 py-3.5 rounded-xl border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-[#0A2C93] focus:border-transparent"
                  data-testid="phone-input" />
              </div>

              <button onClick={handleSendOTP} disabled={loading || phone.replace(/\D/g, '').length !== 10}
                className="w-full py-3.5 rounded-xl bg-[#0A2C93] hover:bg-[#082273] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold transition flex items-center justify-center gap-2"
                data-testid="send-otp-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><span>Send OTP</span><ArrowRight className="w-4 h-4" /></>}
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400">or continue with</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              {/* Google Sign In */}
              <button onClick={handleGoogleSignIn} disabled={loading}
                className="w-full py-3 rounded-xl border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700 transition flex items-center justify-center gap-3"
                data-testid="google-signin-btn">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>

              {/* Email option */}
              <button onClick={() => setStep('google-email')}
                className="w-full py-3 mt-2 rounded-xl border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700 transition flex items-center justify-center gap-3"
                data-testid="email-signin-btn">
                <Mail className="w-5 h-5 text-slate-500" />
                Continue with Email
              </button>
            </div>
          )}

          {/* ── STEP: OTP Verification ── */}
          {step === 'otp' && (
            <div data-testid="step-otp">
              <h2 className="text-xl font-bold text-slate-900 mb-1">Verify OTP</h2>
              <p className="text-sm text-slate-500 mb-6">
                Enter 4-digit OTP sent to <span className="font-medium text-slate-700">+91 {phone}</span>
              </p>

              <div className="flex gap-3 justify-center mb-6" onPaste={handleOTPPaste}>
                {otp.map((digit, i) => (
                  <input key={i} ref={otpRefs[i]} type="text" inputMode="numeric" maxLength={1}
                    value={digit} onChange={e => handleOTPChange(i, e.target.value)} onKeyDown={e => handleOTPKeyDown(i, e)}
                    className="w-14 h-14 text-center text-2xl font-bold rounded-xl border-2 border-slate-200 focus:border-[#0A2C93] focus:ring-2 focus:ring-[#0A2C93]/20 focus:outline-none transition"
                    data-testid={`otp-input-${i}`} />
                ))}
              </div>

              <button onClick={() => handleVerifyOTP(otp.join(''))} disabled={loading || otp.some(d => !d)}
                className="w-full py-3.5 rounded-xl bg-[#0A2C93] hover:bg-[#082273] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold transition flex items-center justify-center gap-2"
                data-testid="verify-otp-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify'}
              </button>

              <div className="text-center mt-4">
                {resendTimer > 0 ? (
                  <span className="text-sm text-slate-400">Resend OTP in {resendTimer}s</span>
                ) : (
                  <button onClick={handleResendOTP} disabled={loading} className="text-sm text-[#0A2C93] font-medium hover:text-[#082273]" data-testid="resend-otp-btn">
                    Resend OTP
                  </button>
                )}
              </div>

              <button onClick={() => { setStep('phone'); setOtp(['', '', '', '']); }}
                className="text-sm text-slate-500 hover:text-slate-700 mt-3 mx-auto block">
                Change number
              </button>

              {/* Fallback: Other login methods */}
              <div className="mt-5 pt-4 border-t border-slate-100">
                <p className="text-xs text-slate-400 text-center mb-3">Didn't receive OTP? Try another method</p>
                <div className="flex gap-2">
                  <button onClick={handleGoogleSignIn} disabled={loading}
                    className="flex-1 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-xs font-medium text-slate-600 flex items-center justify-center gap-2"
                    data-testid="otp-fallback-google">
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Google
                  </button>
                  <button onClick={() => setStep('google-email')}
                    className="flex-1 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-xs font-medium text-slate-600 flex items-center justify-center gap-2"
                    data-testid="otp-fallback-email">
                    <Mail className="w-4 h-4" /> Email
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP: Email Login/Signup ── */}
          {step === 'google-email' && (
            <div data-testid="step-email">
              <h2 className="text-xl font-bold text-slate-900 mb-1">{emailMode === 'signup' ? 'Create Account' : 'Sign In with Email'}</h2>
              <p className="text-sm text-slate-500 mb-6">{emailMode === 'signup' ? 'Create a new account' : 'Enter your email and password'}</p>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com" autoFocus
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#0A2C93] text-base"
                    data-testid="email-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Password</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                    placeholder={emailMode === 'signup' ? 'Min 6 characters' : 'Enter password'}
                    onKeyDown={e => e.key === 'Enter' && handleEmailAuth()}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#0A2C93] text-base"
                    data-testid="password-input" />
                </div>
              </div>

              <button onClick={handleEmailAuth} disabled={loading || !email || !password}
                className="w-full py-3.5 rounded-xl bg-[#0A2C93] hover:bg-[#082273] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold transition flex items-center justify-center gap-2"
                data-testid="email-submit-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : emailMode === 'signup' ? 'Create Account' : 'Sign In'}
              </button>

              <p className="text-center text-sm text-slate-500 mt-4">
                {emailMode === 'login' ? "Don't have an account? " : "Already have an account? "}
                <button onClick={() => setEmailMode(emailMode === 'login' ? 'signup' : 'login')}
                  className="text-[#0A2C93] font-medium">
                  {emailMode === 'login' ? 'Sign Up' : 'Sign In'}
                </button>
              </p>

              <button onClick={() => setStep('phone')} className="text-sm text-slate-500 hover:text-slate-700 mt-3 mx-auto block">
                Back to phone login
              </button>
            </div>
          )}

          {/* ── STEP: Add Phone (for Google/Email users) — OTP verified ── */}
          {step === 'add-phone' && (
            <div data-testid="step-add-phone">
              <h2 className="text-xl font-bold text-slate-900 mb-1">Add Mobile Number</h2>
              <p className="text-sm text-slate-500 mb-6">We'll send an OTP to verify this number. It's required for booking confirmations and game updates.</p>

              <label className="text-sm font-medium text-slate-700 mb-2 block">Mobile Number</label>
              <div className="flex gap-2 mb-4">
                <div className="flex items-center px-3.5 bg-slate-100 rounded-xl text-sm font-medium text-slate-600 border border-slate-200">+91</div>
                <input type="tel" inputMode="numeric" value={collectPhone}
                  onChange={e => { setCollectPhone(e.target.value.replace(/\D/g, '').slice(0, 10)); if (phoneOtpSent) { setPhoneOtpSent(false); setCollectPhoneOtp(''); } }}
                  disabled={phoneOtpSent}
                  placeholder="Enter 10-digit number" autoFocus
                  className="flex-1 px-4 py-3.5 rounded-xl border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-[#0A2C93] focus:border-transparent disabled:bg-slate-50"
                  data-testid="add-phone-input" />
              </div>

              {!phoneOtpSent ? (
                <button onClick={handleSendAddPhoneOtp} disabled={loading || collectPhone.replace(/\D/g, '').length !== 10}
                  className="w-full py-3.5 rounded-xl bg-[#0A2C93] hover:bg-[#082273] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold transition flex items-center justify-center gap-2"
                  data-testid="add-phone-send-otp-btn">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><span>Send OTP</span><ArrowRight className="w-4 h-4" /></>}
                </button>
              ) : (
                <>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Enter OTP</label>
                  <input type="tel" inputMode="numeric" value={collectPhoneOtp}
                    onChange={e => setCollectPhoneOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onKeyDown={e => e.key === 'Enter' && collectPhoneOtp.replace(/\D/g, '').length >= 4 && handleAddPhone()}
                    placeholder="Enter OTP" autoFocus
                    className="w-full mb-3 px-4 py-3.5 rounded-xl border border-slate-200 text-base tracking-widest focus:outline-none focus:ring-2 focus:ring-[#0A2C93] focus:border-transparent"
                    data-testid="add-phone-otp-input" />
                  <div className="flex items-center justify-between mb-4">
                    <button onClick={() => { setPhoneOtpSent(false); setCollectPhoneOtp(''); }}
                      className="text-sm text-slate-500 hover:text-slate-700" data-testid="add-phone-change-number">Change number</button>
                    <button onClick={handleSendAddPhoneOtp} disabled={resendTimer > 0 || loading}
                      className="text-sm text-[#0A2C93] disabled:text-slate-400" data-testid="add-phone-resend-otp">
                      {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}</button>
                  </div>
                  <button onClick={handleAddPhone} disabled={loading || collectPhoneOtp.replace(/\D/g, '').length < 4}
                    className="w-full py-3.5 rounded-xl bg-[#0A2C93] hover:bg-[#082273] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold transition flex items-center justify-center gap-2"
                    data-testid="add-phone-btn">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><span>Verify & Continue</span><ArrowRight className="w-4 h-4" /></>}
                  </button>
                </>
              )}
            </div>
          )}

          {/* ── STEP: Complete Profile (new users) ── */}
          {step === 'signup' && (
            <div data-testid="step-signup">
              <h2 className="text-xl font-bold text-slate-900 mb-1">Complete Your Profile</h2>
              <p className="text-sm text-slate-500 mb-6">Quick details to personalize your experience</p>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Name <span className="text-red-500">*</span></label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Enter your name" autoFocus
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#0A2C93] text-base" data-testid="signup-name" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Gender <span className="text-red-500">*</span></label>
                  <div className="flex gap-2">
                    {['Male', 'Female', 'Other'].map(g => (
                      <button key={g} onClick={() => setGender(g.toLowerCase())}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-medium border-2 transition ${gender === g.toLowerCase() ? 'border-[#0A2C93] bg-[#0A2C93]/5 text-[#0A2C93]' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                        data-testid={`gender-${g.toLowerCase()}`}>{g}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Sports <span className="text-slate-400 text-xs">(Optional)</span></label>
                  <div className="flex flex-wrap gap-2">
                    {SPORTS_OPTIONS.map(sport => (
                      <button key={sport} onClick={() => toggleSport(sport)}
                        className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition ${selectedSports.includes(sport) ? 'border-[#0A2C93] bg-[#0A2C93]/5 text-[#0A2C93]' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                        data-testid={`sport-${sport.toLowerCase()}`}>
                        {selectedSports.includes(sport) && <Check className="w-3.5 h-3.5 inline mr-1" />}{sport}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email <span className="text-slate-400 text-xs">(Optional)</span></label>
                  <input type="email" value={signupEmail} onChange={e => setSignupEmail(e.target.value)} placeholder="example@gmail.com"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#0A2C93] text-base" data-testid="signup-email" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Age <span className="text-slate-400 text-xs">(Optional)</span></label>
                  <input type="number" value={age} onChange={e => setAge(e.target.value)} placeholder="25" min="10" max="100"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#0A2C93] text-base" data-testid="signup-age" />
                </div>
              </div>

              <button onClick={handleCompleteProfile} disabled={loading || !name.trim()}
                className="w-full mt-6 py-3.5 rounded-xl bg-[#0A2C93] hover:bg-[#082273] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold transition flex items-center justify-center gap-2"
                data-testid="complete-profile-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><span>Continue</span><ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          )}
        </div>
        <div className="h-2 sm:h-0" />
      </div>
    </div>
  );
};

export default OTPLoginModal;
